import { Controller } from '@hotwired/stimulus';
import { refreshCsrfToken, submitWithFreshCsrfToken } from '../lib/fresh_csrf';

/**
 * Inline gated-tool signup dialog: Google hero, email behind one reveal.
 *
 * Guest tool pages are served from the Cloudflare edge cache, so the authenticity
 * tokens baked into the dialog's forms belong to whichever session warmed the cache
 * and a cookie-less visitor's POST would 422. Opening the dialog fetches a fresh
 * token (which also starts a session and stores this page as the return path) and
 * swaps it into every form; a submit that beats the fetch waits for it (lib/fresh_csrf.js).
 */
export default class extends Controller {
  static targets = [
    'dialog',
    'signupPanel',
    'loginPanel',
    'signupTab',
    'loginTab',
    'signupEmailBlock',
    'loginEmailBlock',
    'emailAlternate',
    'emailAlternateToggle',
  ];

  open(event) {
    event?.preventDefault();
    const mode = event?.params?.mode === 'login' ? 'login' : 'signup';
    const expandEmail = event?.params?.expandEmail === true || event?.params?.expandEmail === 'true';
    // A page with more than one free sheet (e.g. /habits) says which one to unlock after sign-up.
    if (event?.params?.pendingUnlock) this.setPendingUnlock(event.params.pendingUnlock);
    this.applyMode(mode);
    this.collapseEmailAlternate();
    if (expandEmail) {
      this.revealEmailAlternate();
      this.applyMode(mode);
      this.revealEmailForMode(mode);
    }
    this.dialogTarget.showModal();
    this.refreshCsrfToken();
  }

  setPendingUnlock(slug) {
    const url = new URL(this.dialogTarget.dataset.csrfRefreshUrl, window.location.origin);
    url.searchParams.set('pending_unlock', slug);
    this.dialogTarget.dataset.csrfRefreshUrl = url.pathname + url.search;
  }

  refreshCsrfToken() {
    return refreshCsrfToken(this.dialogTarget.dataset.csrfRefreshUrl, {
      returnTo: window.location.pathname + window.location.search,
    });
  }

  // Bubbled submit from any dialog form. The Google popup form handles its own submit
  // (defaultPrevented) and relies on the refresh started when the dialog opened.
  ensureFreshToken(event) {
    submitWithFreshCsrfToken(event, this.dialogTarget.dataset.csrfRefreshUrl, {
      returnTo: window.location.pathname + window.location.search,
    });
  }

  close() {
    this.dialogTarget.close();
    this.collapseEmailAlternate();
  }

  backdropClose(event) {
    if (event.target === this.dialogTarget) {
      this.dialogTarget.close();
      this.collapseEmailAlternate();
    }
  }

  showSignup(event) {
    event?.preventDefault();
    this.applyMode('signup');
    if (this.isEmailAlternateVisible()) {
      this.revealEmailForMode('signup');
    }
  }

  showLogin(event) {
    event?.preventDefault();
    this.applyMode('login');
    if (this.isEmailAlternateVisible()) {
      this.revealEmailForMode('login');
    }
  }

  revealEmailAlternate(event) {
    event?.preventDefault();
    this.revealEmailAlternateSection();
    this.revealEmailForMode(this.activeMode());
  }

  applyMode(mode) {
    const isLogin = mode === 'login';

    this.signupPanelTarget.hidden = isLogin;
    this.signupPanelTarget.classList.toggle('hidden', isLogin);
    this.loginPanelTarget.hidden = !isLogin;
    this.loginPanelTarget.classList.toggle('hidden', !isLogin);

    this.signupTabTarget.setAttribute('aria-selected', isLogin ? 'false' : 'true');
    this.signupTabTarget.tabIndex = isLogin ? -1 : 0;
    this.loginTabTarget.setAttribute('aria-selected', isLogin ? 'true' : 'false');
    this.loginTabTarget.tabIndex = isLogin ? 0 : -1;
  }

  activeMode() {
    return this.loginTabTarget.getAttribute('aria-selected') === 'true' ? 'login' : 'signup';
  }

  isEmailAlternateVisible() {
    return this.hasEmailAlternateTarget && !this.emailAlternateTarget.hidden;
  }

  collapseEmailAlternate() {
    if (!this.hasEmailAlternateTarget) return;

    this.emailAlternateTarget.hidden = true;
    this.emailAlternateTarget.classList.add('hidden');

    if (this.hasEmailAlternateToggleTarget) {
      this.emailAlternateToggleTarget.hidden = false;
      this.emailAlternateToggleTarget.classList.remove('hidden');
    }
  }

  revealEmailAlternateSection() {
    if (!this.hasEmailAlternateTarget) return;

    this.emailAlternateTarget.hidden = false;
    this.emailAlternateTarget.classList.remove('hidden');

    if (this.hasEmailAlternateToggleTarget) {
      this.emailAlternateToggleTarget.hidden = true;
      this.emailAlternateToggleTarget.classList.add('hidden');
    }
  }

  revealEmailForMode(mode) {
    if (!this.hasSignupEmailBlockTarget) return;

    const isLogin = mode === 'login';
    this.signupEmailBlockTarget.hidden = isLogin;
    this.signupEmailBlockTarget.classList.toggle('hidden', isLogin);
    this.loginEmailBlockTarget.hidden = !isLogin;
    this.loginEmailBlockTarget.classList.toggle('hidden', !isLogin);

    requestAnimationFrame(() => {
      const block = isLogin ? this.loginEmailBlockTarget : this.signupEmailBlockTarget;
      const field = block.querySelector('input:not([type="hidden"])');
      field?.focus();
    });
  }
}
